import { ForwardedRef, SyntheticEvent, forwardRef, useState } from "react";
import { LocationOn } from "@mui/icons-material";
import {
  Autocomplete,
  CircularProgress,
  FilterOptionsState,
  TextField,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import { useDebounce } from "src/hooks/debounceHooks";
import type { GeopuntLocation } from "src/types/geopunt";
import { useGeopuntLocation } from "src/hooks/geopuntHooks";
import { useTranslation } from "react-i18next";

interface Props {
  onSelectLocation: (location: GeopuntLocation) => void;
}

export const LocationAutocomplete = forwardRef(LocationAutocompleteComponent);

export function LocationAutocompleteComponent(props: Props, ref: ForwardedRef<HTMLDivElement>) {
  const { onSelectLocation } = props;
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [selectedValue, setSelectedValue] = useState<GeopuntLocation | null>(null);
  const debouncedQuery = useDebounce(query, 500).trim();

  const { data: locations, isFetching } = useGeopuntLocation(debouncedQuery, {
    enabled: debouncedQuery.length > 2,
  });
  function getOptions() {
    if (locations && selectedValue)
      return [...locations.filter((location) => location.id !== selectedValue.id), selectedValue];
    if (!locations && selectedValue) return [selectedValue];
    if (locations && !selectedValue) return locations;
    return [];
  }

  return (
    <StyledAutocomplete<GeopuntLocation>
      disablePortal
      size="medium"
      ref={ref}
      value={selectedValue}
      onChange={(_e, location) => {
        if (!location) return;
        onSelectLocation(location);
        setSelectedValue(location);
      }}
      className="focus-autocomplete"
      loading={isFetching}
      filterOptions={(options: GeopuntLocation[], state: FilterOptionsState<GeopuntLocation>) => {
        const arrayOfSplitString: string[] = state.inputValue.trim().split(/\W+/);
        const filtered = options.filter((option) => {
          const optionString = `${option.thoroughFarename} ${option.municipality}`.replaceAll("null", "").toLowerCase();
          return arrayOfSplitString.some((word) => {
            return optionString.includes(word.toLocaleLowerCase());
          });
        });
        return filtered;
      }}
      loadingText={<CircularProgress size={20} />}
      noOptionsText={<Typography>{t("label.noResultsFound")}</Typography>}
      onInputChange={(_: SyntheticEvent<Element, Event>, newInputValue: string) => {
        setQuery(newInputValue);
      }}
      getOptionLabel={(option) => {
        if (option.thoroughFarename === null) return option.municipality;
        return `${option.thoroughFarename}, ${option.municipality}`;
      }}
      PaperComponent={StyledPaper}
      options={getOptions()}
      renderOption={(props, option, { selected }) => {
        return (
          <Styles {...props} key={option.id} className={selected ? "selected" : "unselected"}>
            <LocationOn className="icon" fontSize="small" />
            <>
              {option.thoroughFarename && (
                <span className="flex-option">
                  <Typography>{`${option?.thoroughFarename}`}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {option?.municipality}
                  </Typography>
                </span>
              )}
              {option.thoroughFarename === null && (
                <Typography color="textSecondary">{option?.municipality}</Typography>
              )}
            </>
          </Styles>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
          }}
          placeholder={t("label.searchLocation")}
        />
      )}
    />
  );
}

const StyledAutocomplete = styled(Autocomplete)<GeopuntLocation>`
  min-width: 350px;
  position: relative;
  z-index: 1500;
  box-shadow: ${({ theme }) => theme.shadows[2]};
  .MuiAutocomplete-inputRoot {
    margin-bottom: 0;
    &:focus-visible {
      outline: unset;
    }
    &.Mui-focused {
      outline: unset;
      box-shadow: 0px 0px 0px 3px ${({ theme }) => theme.palette.primary.main + "4D"};
    }
    background-color: ${({ theme }) => theme.palette.background.paper};
  }
` as unknown as typeof Autocomplete;

const StyledPaper = styled("div")`
  border-radius: 0;
  background-color: ${({ theme }) => theme.palette.background.paper};
  margin-top: -16px;
  padding-top: 16px;
  box-shadow: ${({ theme }) => theme.shadows[1]};
`;

const Styles = styled("li")`
  max-width: 100%;
  padding: ${({ theme }) => `${theme.spacing(1, 1.5)}`};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  cursor: pointer;

  .flex-option {
    display: flex;
    flex-direction: column;
  }

  &.Mui-focused {
    background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.05)};
  }

  & > svg {
    grid-column: 1;
    grid-row: 1 / -1;
    align-self: center;
    justify-self: center;
  }
`;
